// src/encounter/services/use-favourites.ts
// ADD / REMOVE a provider favourite (PROD-1839, flow 3 — the "Add to favourites" modal).
//
//   POST /api/Provider/Favourite{Type}   body: an ARRAY of items
//
// Flutter: `favouriteProviderIcd` and its five siblings (encounter_data_source_impl.dart:259-340)
// each POST `items.map(toMap).toList()` — a LIST, not a single object, even for one item.
//
// REMOVE IS THE SAME CALL. Every item's `toMap` carries `isDeleted` (provider_icd.dart:69-76), so
// un-favouriting is the same POST with the flag set — the identical upsert-plus-soft-delete shape
// the bundle endpoint uses. There is no DELETE route.
//
// SIX endpoints exist, one per catalog type. `referral` and `education` have NONE — Flutter never
// favourites them — so this map has no entry and the modal hides the affordance for those tabs
// rather than posting to a route that does not exist.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { ApSearchCategory } from '@scripsteam/scrips-signal-ds';

/** Catalog type → the endpoint segment Flutter uses. Absent = the type cannot be favourited. */
const FAVOURITE_ENDPOINT: Partial<Record<ApSearchCategory, string>> = {
  diagnosis: 'FavouriteICD',
  medication: 'FavouriteMedication',
  procedure: 'FavouriteProcedure',
  lab: 'FavouriteLabOrder',
  imaging: 'FavouriteRadiology',
  // `FavouriteSnomed` also exists; it is reached through the diagnosis tab's SNOMED results, which
  // this app routes via FavouriteICD today. Left out until a SNOMED favourite is actually offered.
};

/**
 * Medication favourites need the SERVER's shape (Scrips.Provider `ProviderMedicationRequestModel`:
 * `{ Id: Guid?, MedicationCode: { Code, Display, System }, Provider, Notes, IsDeleted }`).
 * `PostMedicationAsync` ignores Id (it de-dupes on Provider|Display) but dereferences MedicationCode.
 * The rows here are flat (a MedicationOption's `name`, a favourite's `display`), and a catalogue
 * row's `id` is its DDC code, which a Guid? refuses. So the body is rebuilt, with no id.
 */
function toMedicationFavourite(item: Record<string, unknown>): Record<string, unknown> {
  const s = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  return {
    id: null,
    medicationCode: { code: s(item.code), display: s(item.name) || s(item.display), system: s(item.system) },
  };
}

/** True when this catalog type can be favourited at all — drives whether the ☆ renders. */
export function canFavourite(category: ApSearchCategory): boolean {
  return FAVOURITE_ENDPOINT[category] != null;
}

export interface FavouriteVars {
  category: ApSearchCategory;
  /** The provider whose favourites these are. */
  providerId: string;
  /** The RAW catalog row (the search result), round-tripped as the item body. */
  item: Record<string, unknown>;
  /** True to un-favourite — the same POST with isDeleted set. */
  remove?: boolean;
}

/**
 * Add or remove one favourite, then refresh the catalog searches that read them.
 *
 * The favourites list is what the rail's FAVOURITE block renders, so the invalidation is what makes
 * the ☆ visibly take effect; without it the modal would look inert until a remount.
 */
export function useFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ category, providerId, item, remove = false }: FavouriteVars) => {
      const endpoint = FAVOURITE_ENDPOINT[category];
      // Guard rather than build "/api/Provider/undefined" — a 404 here would read as "save failed"
      // when the truth is the type has no favourites route at all.
      if (!endpoint) throw new Error(`No favourites endpoint for category "${category}"`);
      const body = category === 'medication' ? toMedicationFavourite(item) : item;
      await post(`/api/Provider/${endpoint}`, [
        { ...body, provider: providerId, isDeleted: remove },
      ]);
    },
    onSuccess: (_d, vars) => {
      // The READ side is use-provider-favourites, keyed ['provider-favourites', category, providerId]
      // (:95). Invalidating that exact key is what makes the star visibly take effect — without it
      // the modal looks inert until a remount.
      void qc.invalidateQueries({
        queryKey: ['provider-favourites', vars.category, vars.providerId],
      });
    },
  });
}
